import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Card, Form, Input, Select, Upload, Breadcrumb, Row, Col, Radio, Checkbox, Image, Alert } from 'antd';
import { showConfirmModal, showInfoModalWithCheckbox } from '../components/PopupModal';
import { UploadOutlined, InfoCircleOutlined, EyeOutlined, DeleteOutlined, CloseCircleOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { tenantService, Tenant } from '../services/tenants';
import { uploadService, UploadedFile } from '../services/upload';
import type { UploadFile, UploadProps } from 'antd';
import authService from '../services/auth';
import TenantPreviewModal from '../components/TenantPreviewModal';
import api from '../services/api';
import { message } from '../utils/message';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const TenantIntroEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { clearAllBlockers } = useNavigationBlockerContext();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isEventContent, setIsEventContent] = useState(false);
  const [attachedFileList, setAttachedFileList] = useState<UploadFile[]>([]);
  const [additionalFileList, setAdditionalFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedAttachedFile, setUploadedAttachedFile] = useState<UploadedFile | null>(null);
  const [uploadedAdditionalFile, setUploadedAdditionalFile] = useState<UploadedFile | null>(null);
  const [layoutOptions, setLayoutOptions] = useState<string[]>([]);
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [isMainImageDeleted, setIsMainImageDeleted] = useState(false);
  const [isThumbnailDeleted, setIsThumbnailDeleted] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [isModified, setIsModified] = useState(false);

  // 네비게이션 가드 적용
  useNavigationBlocker(isModified, '/tenant-intro');

  const user = authService.getCurrentUser();
  const isSuperAdmin = user?.role === 'super_admin';

  // 입주사 정보 및 레이아웃 옵션 불러오기
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        // 레이아웃 옵션 가져오기 (에러 시 기본값 사용)
        try {
          const layoutsResponse = await tenantService.getLayouts();
          setLayoutOptions(layoutsResponse.data || ['layout1', 'layout2']);
        } catch (layoutError) {
          console.warn('Failed to fetch layouts, using default options:', layoutError);
          // 레이아웃 API 실패 시 기본값 사용
          setLayoutOptions(['layout1', 'layout2']);
        }

        // 입주사명 옵션 가져오기 (입주사 관리에서 등록된 회사명)
        try {
          const response = await api.get('/tenant-units');
          const data = response.data;

          if (data.success && data.data) {
            // 모든 층의 rooms에서 companyName 추출
            const companyNames = new Set<string>();
            data.data.forEach((floor: any) => {
              floor.rooms.forEach((room: any) => {
                if (room.companyName && room.companyName.trim() !== '') {
                  companyNames.add(room.companyName);
                }
              });
            });

            const options = Array.from(companyNames)
              .sort()
              .map(name => ({
                value: name,
                label: name,
              }));

            setCompanyOptions(options);
          }
        } catch (error) {
          console.error('Failed to load company names:', error);
        }

        // 입주사 정보 가져오기
        const response = await tenantService.getTenant(parseInt(id));
        const tenantData = response.data;
        setTenant(tenantData);

        // 이벤트 콘텐츠 여부 설정
        setIsEventContent(tenantData.isEventContent || false);
        setApprovalStatus(tenantData.approvalStatus || 'pending');
        setRejectionReason(tenantData.rejectionReason || '');

        // 폼에 데이터 설정
        form.setFieldsValue({
          layout: tenantData.layout || 'layout1',
          exposure: tenantData.status === 'active' ? 'show' : 'hide',
          companyName: tenantData.name || '',
          phoneNumber: tenantData.phone || '',
          content: tenantData.description || '',
        });
      } catch (error) {
        message.error('입주사 정보를 불러오는데 실패했습니다.');
        console.error('Error fetching tenant:', error);
        navigate('/tenant-intro');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, form, navigate]);

  // 첨부파일 업로드 처리
  const handleAttachedFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);
      const validation = uploadService.validateFile(file as File);

      if (!validation.valid) {
        message.error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }

      const response = await uploadService.uploadSingleImage(file as File);

      if (response.success) {
        setUploadedAttachedFile(response.data);
        setIsMainImageDeleted(false); // 새 파일 업로드 시 삭제 상태 초기화
        message.success('파일이 성공적으로 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error('파일 업로드에 실패했습니다.');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // 추가 첨부파일 업로드 처리
  const handleAdditionalFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);
      const validation = uploadService.validateFile(file as File);

      if (!validation.valid) {
        message.error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }

      const response = await uploadService.uploadSingleImage(file as File);

      if (response.success) {
        setUploadedAdditionalFile(response.data);
        setIsThumbnailDeleted(false); // 새 파일 업로드 시 삭제 상태 초기화
        message.success('파일이 성공적으로 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error('파일 업로드에 실패했습니다.');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleAttachedFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setAttachedFileList(fileList);
  };

  const handleAdditionalFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setAdditionalFileList(fileList);
  };

  const handleRemoveAttachedFile = () => {
    setAttachedFileList([]);
    setUploadedAttachedFile(null);
    setIsMainImageDeleted(true);
    // 기존 이미지도 제거하기 위해 tenant의 imageUrl을 null로 설정
    if (tenant) {
      setTenant({ ...tenant, imageUrl: undefined });
    }
  };

  const handleRemoveAdditionalFile = () => {
    setAdditionalFileList([]);
    setUploadedAdditionalFile(null);
    setIsThumbnailDeleted(true);
    // 기존 이벤트 이미지도 제거하기 위해 tenant의 thumbnailUrl을 null로 설정
    if (tenant) {
      setTenant({ ...tenant, thumbnailUrl: undefined });
    }
  };

  // 입주사 설명 변경 핸들러 (Enter 키로만 줄바꿈)
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    // 최대 3줄로 제한
    const lines = value.split('\n');

    if (lines.length > 3) {
      // 3줄 초과 시 마지막 줄 제거
      const limitedLines = lines.slice(0, 3);
      const limitedValue = limitedLines.join('\n');
      form.setFieldsValue({ content: limitedValue });
      message.warning('최대 3줄까지만 입력 가능합니다.');
    } else {
      // 사용자가 입력한 값 그대로 저장 (Enter로 입력한 \n 포함)
      form.setFieldsValue({ content: value });
    }
  };

  // 대표번호 검증 핸들러
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // '-' 포함 여부 확인
    if (value.includes('-')) {
      setPhoneError("'-'를 제외하고 입력해주세요.");
    } else if (value && !/^\d*$/.test(value)) {
      // 숫자가 아닌 문자 확인
      setPhoneError('숫자만 입력 가능합니다.');
    } else {
      setPhoneError('');
    }

    // 폼 값 업데이트
    form.setFieldsValue({ phoneNumber: value });
  };

  // 이벤트 콘텐츠 체크박스 변경 핸들러
  const handleEventContentChange = (checked: boolean) => {
    if (checked) {
      // 체크하는 경우 - 안내 모달 표시 (다시 보지 않기 옵션)
      const hideEventContentInfo = localStorage.getItem('hideEventContentInfo');

      if (hideEventContentInfo === 'true') {
        // 다시 보지 않기를 선택한 경우 바로 체크
        setIsEventContent(true);
      } else {
        // 안내 모달 표시
        showInfoModalWithCheckbox({
          title: '안내',
          content: '입주사 이벤트 콘텐츠 추가 시 승인요청 후\nHNINE 문의 바랍니다.\n02-0000-0000',
          onConfirm: () => {
            setIsEventContent(true);
          },
          onDontShowAgain: (dontShow) => {
            if (dontShow) {
              localStorage.setItem('hideEventContentInfo', 'true');
            }
          },
        });
      }
    } else {
      // 체크 해제하는 경우 - 파일이 업로드된 경우 확인
      const hasFile = uploadedAdditionalFile || (!isThumbnailDeleted && tenant?.thumbnailUrl);

      if (hasFile) {
        showConfirmModal({
          title: '알림',
          content: '첨부한 파일이 삭제됩니다.\n계속 진행하시겠습니까?',
          okText: '확인',
          cancelText: '취소',
          onConfirm: () => {
            // 확인을 누르면 파일 삭제 및 체크 해제
            handleRemoveAdditionalFile();
            setIsEventContent(false);
          },
          onCancel: () => {
            // 취소를 누르면 아무것도 하지 않음 (체크 상태 유지)
          },
        });
      } else {
        // 파일이 없는 경우 바로 체크 해제
        setIsEventContent(false);
      }
    }
  };

  const handlePreview = () => {
    const values = form.getFieldsValue();

    if (!values.companyName) {
      message.warning('입주사명을 입력해주세요.');
      return;
    }

    const hasMainImage = uploadedAttachedFile || (!isMainImageDeleted && tenant?.imageUrl);
    if (!hasMainImage) {
      message.warning('입주사 이미지를 첨부해주세요.');
      return;
    }

    setPreviewModalVisible(true);
  };

  const getPreviewTenantData = (): Tenant => {
    const values = form.getFieldsValue();
    const previewId = tenant?.id ?? (id ? Number(id) : 0);

    return {
      id: previewId,
      name: values.companyName || tenant?.name || '입주사명',
      phone: values.phoneNumber || tenant?.phone || '',
      description: values.content || tenant?.description || '',
      imageUrl: uploadedAttachedFile?.url || (!isMainImageDeleted && tenant?.imageUrl) || '',
      thumbnailUrl: isEventContent ? (uploadedAdditionalFile?.url || (!isThumbnailDeleted && tenant?.thumbnailUrl) || undefined) : undefined,
      isEventContent: isEventContent,
      layout: (values.layout || tenant?.layout || 'layout2') as 'layout1' | 'layout2',
      approvalStatus: tenant?.approvalStatus || 'pending',
      status: (values.exposure === 'show' ? 'active' : 'inactive') as 'active' | 'inactive' | 'closed',
      createdAt: tenant?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      rejectionReason: tenant?.rejectionReason,
      // tenantUnits를 제거하여 모달에서 선택한 회사명으로 API 조회하도록 함
    };
  };

  const handleSave = async () => {
    try {
      // 대표번호 에러 체크
      if (phoneError) {
        message.error(phoneError);
        return;
      }

      const values = await form.validateFields();

      if (!id) return;

      // 필수 이미지 검증 (삭제된 경우도 고려)
      if (isMainImageDeleted || (!uploadedAttachedFile && !tenant?.imageUrl)) {
        message.error('입주사 이미지를 첨부해주세요.');
        return;
      }

      // 이벤트 콘텐츠인 경우 추가 첨부파일 필수 (삭제된 경우도 고려)
      if (isEventContent && (isThumbnailDeleted || (!uploadedAdditionalFile && !tenant?.thumbnailUrl))) {
        message.error('이벤트 콘텐츠 첨부파일을 업로드해주세요.');
        return;
      }

      // 수퍼관리자인 경우 자동 승인 확인 팝업 표시
      if (isSuperAdmin) {
        showConfirmModal({
          title: '알림',
          content: '저장 시 콘텐츠가 자동 승인됩니다.\n저장하시겠습니까?',
          okText: '확인',
          cancelText: '취소',
          onConfirm: async () => {
            await performSave(values);
          },
        });
      } else {
        // 일반 관리자는 바로 저장
        await performSave(values);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        message.error('필수 입력 항목을 확인해주세요.');
      } else {
        console.error('Error updating tenant:', error);
        message.error('입주사 정보 수정에 실패했습니다.');
      }
    }
  };

  const performSave = async (values: any) => {
    try {
      if (!id) return;

      setLoading(true);

      // 백엔드 API 형식에 맞게 데이터 변환
      const updateData = {
        name: values.companyName, // companyName -> name으로 변환
        phone: values.phoneNumber, // phoneNumber -> phone으로 변환
        description: values.content, // content -> description으로 변환
        layout: values.layout, // 레이아웃 정보 추가
        isEventContent: isEventContent, // 이벤트 콘텐츠 여부 추가
        status: values.exposure === 'show' ? 'active' as const : 'inactive' as const, // exposure 값에 따라 status 설정
        // 이미지 URL 처리: 삭제된 경우 undefined, 새 이미지가 있으면 새 이미지, 그렇지 않으면 기존 이미지
        imageUrl: isMainImageDeleted ? undefined : (uploadedAttachedFile?.url || tenant?.imageUrl),
        // 썸네일 URL 처리: 삭제된 경우 undefined, 새 썸네일이 있으면 새 썸네일, 그렇지 않으면 기존 썸네일
        thumbnailUrl: isThumbnailDeleted ? undefined : (uploadedAdditionalFile?.url || tenant?.thumbnailUrl),
        // 수퍼 관리자는 자동 승인, 일반 관리자는 승인 대기
        approvalStatus: (isSuperAdmin ? 'approved' : 'pending') as 'approved' | 'pending',
      };

      await tenantService.updateTenant(parseInt(id), updateData);

      message.success('성공적으로 저장되었습니다.');

      // 모든 네비게이션 가드 강제 해제 후 이동
      clearAllBlockers();
      navigate('/tenant-intro');
    } catch (error) {
      console.error('Error updating tenant:', error);
      message.error('입주사 정보 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          // navigate 전에 isModified를 false로 설정하여 useNavigationBlocker를 비활성화
          setIsModified(false);
          // setTimeout으로 상태 업데이트가 완료된 후 네비게이션 실행
          setTimeout(() => {
            navigate('/tenant-intro');
          }, 0);
        },
      });
    } else {
      navigate('/tenant-intro');
    }
  };

  const renderApprovalAlert = () => {
    let alertMessage = '';
    let alertType: 'info' | 'warning' | 'error' = 'info';
    let icon = <InfoCircleOutlined />;

    const isSuperAdmin = authService.isSuperAdmin();

    switch (approvalStatus) {
      case 'pending':
        if (isSuperAdmin) {
          alertMessage = '승인이 필요한 콘텐츠입니다. 검토 후 승인 여부를 진행해주세요.';
          alertType = 'info';
        } else {
          alertMessage = '승인 대기중인 콘텐츠입니다. 수정 및 삭제가 불가합니다.';
          alertType = 'warning';
        }
        icon = <InfoCircleOutlined />;
        break;
      case 'approved':
        alertMessage = '승인 완료된 콘텐츠입니다.';
        alertType = 'info';
        icon = <InfoCircleOutlined />;
        break;
      case 'rejected':
        alertMessage = rejectionReason
          ? `승인 거부된 콘텐츠입니다.\n사유 : ${rejectionReason}`
          : '승인 거부된 콘텐츠입니다.';
        alertType = 'error';
        icon = <CloseCircleOutlined />;
        break;
    }

    return (
      <Alert
        message={alertMessage}
        type={alertType}
        icon={icon}
        showIcon
        style={{ whiteSpace: 'pre-line', marginBottom: '24px' }}
      />
    );
  };

  if (!tenant && !loading) {
    return null;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '건물 안내' },
          { title: '입주사 소개', href: '/tenant-intro' },
          { title: '입주사 소개 수정' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          입주사 소개 수정
        </Title>
        <Button onClick={handlePreview}>미리보기</Button>
      </div>

      {renderApprovalAlert()}

      <Form
        form={form}
        layout="vertical"
        style={{ width: '100%' }}
        onValuesChange={() => setIsModified(true)}
        initialValues={{
          layout: 'layout2',
          exposure: 'show',
          companyName: '',
          phoneNumber: '',
          status: 'active'
        }}
      >
        {/* 기본 정보 */}
        <Card title="기본 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                label={<span>레이아웃명 <span style={{ color: 'red' }}>*</span></span>}
                name="layout"
                required={false}
                rules={[{ required: true, message: '레이아웃을 선택해주세요.' }]}
              >
                <Select placeholder="레이아웃을 선택해주세요" disabled>
                  <Option key="-" value="layout2">-</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span>노출여부 <span style={{ color: 'red' }}>*</span></span>}
                name="exposure"
              >
                <Radio.Group>
                  <Radio value="show">노출</Radio>
                  <Radio value="hide">비노출</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 입주사 정보 */}
        <Card title="입주사 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]} style={{ alignItems: 'flex-end', paddingBottom: '20px' }}>
            <Col span={6}>
              <Form.Item
                label={<span>입주사명 <span style={{ color: 'red' }}>*</span></span>}
                name="companyName"
                required={false}
                rules={[{ required: true, message: '입주사명을 입력해주세요.' }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  showSearch
                  placeholder="회사명을 입력하세요."
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={companyOptions}
                  style={{ width: '100%' }}
                  notFoundContent="등록된 입주사가 없습니다."
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  입주사 관리에 등록된 입주사명만 검색됩니다.
                </Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 16]} style={{ alignItems: 'flex-end' }}>
            <Col span={6}>
              <Form.Item
                label="대표번호"
                name="phoneNumber"
                validateStatus={phoneError ? 'error' : ''}
                help={phoneError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{phoneError}</span> : ''}
                style={{ marginBottom: 0 }}
              >
                <Input onChange={handlePhoneChange} />
              </Form.Item>
            </Col>
            <Col span={16}>
              {!phoneError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    '-' 를 제외하고 입력해주세요.
                  </Text>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* 입주사 설명 */}
        <Card title="입주사 설명" style={{ marginBottom: '24px' }}>
          <Form.Item
            label={<span>내용 <span style={{ color: 'red' }}>*</span></span>}
            name="content"
            required={false}
            rules={[{ required: true, message: '입주사 설명을 입력해주세요.' }, { max: 123, message: '입주사 설명은 123자 이내로 입력해주세요.' }]}
          >
            <TextArea
              rows={3}
              placeholder="내용을 입력하세요."
              showCount
              maxLength={123}
              onKeyDown={(e) => {
                const value = (e.target as HTMLTextAreaElement).value;
                const lineCount = (value.match(/\n/g) || []).length;
                // Enter 키를 누르고 이미 2개의 줄바꿈이 있으면 (총 3줄) 입력 방지
                if (e.key === 'Enter' && lineCount >= 2) {
                  e.preventDefault();
                  message.warning('최대 3줄까지만 입력 가능합니다.');
                }
              }}
              onChange={handleDescriptionChange}
              style={{
                maxWidth: '58ch',
                resize: 'none',
                whiteSpace: 'pre-wrap' // Enter 키로 입력한 줄바꿈 문자(\n) 유지
              }}
            />
          </Form.Item>
        </Card>

        {/* 입주사 이미지 */}
        <Card title="입주사 이미지" style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ display: 'block', marginBottom: '8px', color: '#ffffff' }}>
              <span>첨부파일 <span style={{ color: 'red' }}>*</span></span>
            </Text>

            {/* 기존 이미지 또는 새로 업로드된 이미지 미리보기 */}
            {(uploadedAttachedFile || tenant?.imageUrl) && (
              <div style={{ marginBottom: '16px', border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Text style={{ color: '#ffffff' }}>
                    {uploadedAttachedFile ? '새로 업로드된 파일' : '기존 파일'}
                  </Text>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveAttachedFile}
                    style={{ color: '#ff4d4f' }}
                  >
                    삭제
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <Image
                    width={100}
                    height={100}
                    src={uploadedAttachedFile?.url || tenant?.imageUrl}
                    alt="입주사 이미지"
                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                    preview={{
                      mask: <div><EyeOutlined /> 미리보기</div>
                    }}
                  />
                  <div>
                    <Text style={{ color: '#ffffff', display: 'block' }}>
                      {uploadedAttachedFile?.originalName || '입주사 대표 이미지'}
                    </Text>
                    <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                      {uploadedAttachedFile ?
                        `크기: ${uploadService.formatFileSize(uploadedAttachedFile.size)}` :
                        '기존 등록 이미지'
                      }
                    </Text>
                  </div>
                </div>
              </div>
            )}

            <Dragger
              fileList={attachedFileList}
              onChange={handleAttachedFileChange}
              customRequest={handleAttachedFileUpload}
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              disabled={uploading || !!(uploadedAttachedFile || tenant?.imageUrl)}
              style={{
                background: '#141414',
                border: '2px dashed #303030',
                borderRadius: '8px'
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: '#a6a6a6', fontSize: '48px' }} />
              </p>
              <p style={{ color: '#ffffff', fontSize: '16px', margin: '16px 0 8px' }}>
                Upload
              </p>
            </Dragger>
          </div>
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                파일형식: PNG, JPEG, GIF, WEBP / 50MB 이하 (최대 1개)
              </Text>
            </div>
            <div style={{ marginLeft: '24px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                권장 이미지 크기: 800px(가로) × 600px(세로) 이상
              </Text>
            </div>
          </div>
        </Card>

        {/* 기타 정보 */}
        <Card title="기타 정보" style={{ marginBottom: '32px' }}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <Checkbox
                  checked={isEventContent}
                  onChange={(e) => handleEventContentChange(e.target.checked)}
                >
                  이벤트 콘텐츠
                </Checkbox>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    입주사 관련 이벤트가 있는 경우 승인요청 후 <span style={{ color: '#F24822' }}>HNINE 문의</span> 바랍니다.
                    <span style={{ color: '#F24822' }}> 02-521-0518</span>
                  </Text>
                </div>
              </div>
              {isEventContent && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <Text style={{ display: 'block', marginBottom: '8px', color: '#ffffff' }}>
                      <span>첨부파일 <span style={{ color: 'red' }}>*</span></span>
                    </Text>

                    {/* 기존 이벤트 이미지 또는 새로 업로드된 이미지 미리보기 */}
                    {(uploadedAdditionalFile || tenant?.thumbnailUrl) && (
                      <div style={{ marginBottom: '16px', border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <Text style={{ color: '#ffffff' }}>
                            {uploadedAdditionalFile ? '새로 업로드된 파일' : '기존 파일'}
                          </Text>
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveAdditionalFile}
                            style={{ color: '#ff4d4f' }}
                          >
                            삭제
                          </Button>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          <Image
                            width={100}
                            height={100}
                            src={uploadedAdditionalFile?.url || tenant?.thumbnailUrl}
                            alt="이벤트 이미지"
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                            preview={{
                              mask: <div><EyeOutlined /> 미리보기</div>
                            }}
                          />
                          <div>
                            <Text style={{ color: '#ffffff', display: 'block' }}>
                              {uploadedAdditionalFile?.originalName || '이벤트 콘텐츠 이미지'}
                            </Text>
                            <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                              {uploadedAdditionalFile ?
                                `크기: ${uploadService.formatFileSize(uploadedAdditionalFile.size)}` :
                                '기존 등록 이미지'
                              }
                            </Text>
                          </div>
                        </div>
                      </div>
                    )}

                    <Dragger
                      fileList={additionalFileList}
                      onChange={handleAdditionalFileChange}
                      customRequest={handleAdditionalFileUpload}
                      accept="image/*"
                      maxCount={1}
                      showUploadList={false}
                      disabled={uploading || !!(uploadedAdditionalFile || tenant?.thumbnailUrl)}
                      style={{
                        background: '#141414',
                        border: '2px dashed #303030',
                        borderRadius: '8px'
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: '#a6a6a6', fontSize: '48px' }} />
                      </p>
                      <p style={{ color: '#ffffff', fontSize: '16px', margin: '16px 0 8px' }}>
                        Upload
                      </p>
                    </Dragger>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: '#F24822',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        color: '#ffffff'
                      }}>
                        !
                      </div>
                      <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                        파일형식: PNG, JPEG, GIF, WEBP / 50MB 이하 (최대 1개)
                      </Text>
                    </div>
                    <div style={{ marginLeft: '24px' }}>
                      <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                        권장 이미지 크기: 800px(가로) × 600px(세로) 이상
                      </Text>
                    </div>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          <Button size="large" onClick={handleCancel}>
            취소
          </Button>
          <Button type="primary" size="large" onClick={handleSave} loading={loading}>
            {isSuperAdmin ? '저장' : '승인요청'}
          </Button>
        </div>
      </Form>

      <TenantPreviewModal
        visible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        tenant={getPreviewTenantData()}
      />
    </Space>
  );
};

export default TenantIntroEdit;